<template>
  <div class="sales-step-content">
    <div class="text-center mb-3">
      <font-awesome-icon icon="users" size="3x" class="text-primary mb-2" />
      <h4>Bereich auswählen</h4>
      <p class="text-muted mb-0">
        Wähle einen Bereich aus, den du upgraden möchtest
      </p>
    </div>

    <div v-if="loading" class="text-center my-5">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Bereiche werden geladen...</span>
      </div>
    </div>

    <div v-else-if="error" class="alert alert-danger" role="alert">
      <font-awesome-icon icon="exclamation-triangle" class="me-2" />
      {{ error }}
    </div>

    <div v-else-if="availableTeams.length === 0" class="text-center py-4">
      <font-awesome-icon icon="info-circle" size="2x" class="text-muted mb-2" />
      <h5>Keine upgrade-berechtigten Bereiche</h5>
      <p class="text-muted mb-3">
        Alle deine Bereiche haben bereits einen Pro- oder Ultimate-Plan oder du hast noch keine Bereiche erstellt.
      </p>
      <div class="d-flex gap-2 justify-content-center">
        <router-link to="/teams/create" class="btn btn-primary">
          <font-awesome-icon icon="plus" class="me-2" />
          Neuen Bereich erstellen
        </router-link>
        <router-link to="/teams" class="btn btn-outline-secondary">
          <font-awesome-icon icon="users" class="me-2" />
          Alle Bereiche ansehen
        </router-link>
      </div>
    </div>

    <div v-else>
      <!-- Team Search -->
      <div class="mb-3">
        <div class="input-group">
          <span class="input-group-text">
            <font-awesome-icon icon="search" />
          </span>
          <input
            v-model="searchQuery"
            type="text"
            class="form-control"
            placeholder="Bereich suchen..."
            @input="onSearchInput"
          />
        </div>
        <div v-if="searchQuery" class="mt-2">
          <small class="text-muted">
            {{ pagination.total }} Bereich{{ pagination.total > 1 ? 'e' : '' }} gefunden
          </small>
        </div>
      </div>

      <!-- Team Selection Grid -->
      <div class="row g-3 justify-content-center" :class="{ 'teams-space-around': filteredTeams.length === 2 }">
        <div 
          v-for="team in filteredTeams" 
          :key="team.id"
          class="col-md-6 col-lg-4"
        >
          <div 
            class="card team-selection-card h-100"
            :class="{ 'selected': selectedTeam?.id === team.id }"
            @click="selectTeam(team)"
          >
            <div class="card-body">
              <div class="d-flex align-items-start mb-3">
                <div class="team-avatar me-3">
                  <font-awesome-icon icon="users" size="2x" class="text-primary" />
                </div>
                <div class="flex-grow-1">
                  <h5 class="card-title mb-1">{{ team.name }}</h5>
                  <p class="card-text text-muted small mb-2">{{ team.description || 'Keine Beschreibung' }}</p>
                  <span :class="getPlanBadgeClass(team.status)" class="badge">
                    {{ getPlanDisplayName(team.status) }}
                  </span>
                </div>
                <div class="team-selection-indicator">
                  <font-awesome-icon 
                    v-if="selectedTeam?.id === team.id"
                    icon="check-circle" 
                    class="text-success"
                  />
                  <font-awesome-icon 
                    v-else
                    icon="circle" 
                    class="text-muted"
                  />
                </div>
              </div>

              <!-- Team Stats -->
              <div class="team-stats">
                <div class="row text-center">
                  <div class="col-4">
                    <div class="stat-value">{{ team.memberCount || 0 }}</div>
                    <div class="stat-label">Mitglieder</div>
                  </div>
                  <div class="col-4">
                    <div class="stat-value">{{ team.receiptCount || 0 }}</div>
                    <div class="stat-label">Quittungen</div>
                  </div>
                  <div class="col-4">
                    <div class="stat-value">{{ team.coins || 0 }}</div>
                    <div class="stat-label">Coins</div>
                  </div>
                </div>
              </div>

              <!-- Upgrade Status -->
              <div class="mt-3">
                <div class="alert alert-success alert-sm mb-0">
                  <font-awesome-icon icon="arrow-up" class="me-1" />
                  Upgrade verfügbar
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination Controls -->
      <div v-if="pagination.totalPages > 1" class="mt-3">
        <nav aria-label="Team pagination">
          <ul class="pagination justify-content-center">
            <li class="page-item" :class="{ 'disabled': pagination.currentPage === 1 }">
              <button 
                class="page-link" 
                @click="goToPage(pagination.currentPage - 1)"
                :disabled="pagination.currentPage === 1"
              >
                <font-awesome-icon icon="chevron-left" />
              </button>
            </li>
            
            <li 
              v-for="page in visiblePages" 
              :key="page"
              class="page-item" 
              :class="{ 'active': page === pagination.currentPage }"
            >
              <button 
                class="page-link" 
                @click="goToPage(page)"
              >
                {{ page }}
              </button>
            </li>
            
            <li class="page-item" :class="{ 'disabled': pagination.currentPage === pagination.totalPages }">
              <button 
                class="page-link" 
                @click="goToPage(pagination.currentPage + 1)"
                :disabled="pagination.currentPage === pagination.totalPages"
              >
                <font-awesome-icon icon="chevron-right" />
              </button>
            </li>
          </ul>
        </nav>
        
        <div class="text-center mt-2">
          <small class="text-muted">
            Seite {{ pagination.currentPage }} von {{ pagination.totalPages }}
          </small>
        </div>
      </div>

      <!-- Selection Summary -->
      <div v-if="selectedTeam" class="mt-3">
        <div class="alert alert-success">
          <div class="d-flex align-items-center">
            <font-awesome-icon icon="check-circle" class="me-2" />
            <div>
              <strong>Bereich ausgewählt</strong>
              <div class="small">
                {{ selectedTeam.name }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  availableTeams: {
    type: Array,
    default: () => []
  },
  selectedTeam: {
    type: Object,
    default: null
  },
  loading: {
    type: Boolean,
    default: false
  },
  error: {
    type: String,
    default: null
  }
})

const emit = defineEmits(['team-selected', 'search-changed', 'page-changed'])

const searchQuery = ref('')
const pagination = ref({
  currentPage: 1,
  totalPages: 1,
  total: 0,
  limit: 3
})

const filteredTeams = computed(() => {
  let filtered = props.availableTeams
  if (searchQuery.value) {
    filtered = props.availableTeams.filter(team => 
      team.name.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      (team.description && team.description.toLowerCase().includes(searchQuery.value.toLowerCase()))
    )
  }
  
  // Update pagination based on filtered results
  pagination.value.total = filtered.length
  pagination.value.totalPages = Math.ceil(filtered.length / pagination.value.limit)
  
  // Apply pagination
  const startIndex = (pagination.value.currentPage - 1) * pagination.value.limit
  const endIndex = startIndex + pagination.value.limit
  
  return filtered.slice(startIndex, endIndex)
})

const visiblePages = computed(() => {
  const pages = []
  const current = pagination.value.currentPage
  const total = pagination.value.totalPages
  
  // Show up to 5 page numbers
  let start = Math.max(1, current - 2)
  let end = Math.min(total, start + 4)
  
  // Adjust start if we're near the end
  if (end - start < 4) {
    start = Math.max(1, end - 4)
  }
  
  for (let i = start; i <= end; i++) {
    pages.push(i)
  }
  
  return pages
})

const selectTeam = (team) => {
  emit('team-selected', team)
}

const onSearchInput = () => {
  pagination.value.currentPage = 1
  emit('search-changed', searchQuery.value)
}

const goToPage = (page) => {
  if (page >= 1 && page <= pagination.value.totalPages) {
    pagination.value.currentPage = page
    emit('page-changed', page)
  }
}

const getPlanDisplayName = (status) => {
  switch (status) {
    case 'free':
      return 'Kostenloser Plan'
    case 'pro':
      return 'Pro Plan'
    case 'ultimate':
      return 'Ultimate Plan'
    default:
      return 'Unbekannter Plan'
  }
}

const getPlanBadgeClass = (status) => {
  switch (status) {
    case 'free':
      return 'bg-secondary'
    case 'pro':
      return 'bg-primary'
    case 'ultimate':
      return 'bg-warning text-dark'
    default:
      return 'bg-secondary'
  }
}
</script>

<style scoped>
.team-selection-card {
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  padding: 0.75rem;
}

.team-selection-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  border-color: rgba(13, 110, 253, 0.2);
}

.team-selection-card.selected {
  border-color: var(--bs-primary);
  background: rgba(13, 110, 253, 0.05);
}

.team-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(13, 110, 253, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.team-selection-indicator {
  font-size: 1.5rem;
}

.team-stats {
  border-top: 1px solid #e9ecef;
  padding-top: 1rem;
  margin-top: 1rem;
}

.stat-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--bs-primary);
}

.stat-label {
  font-size: 0.875rem;
  color: #6c757d;
  margin-top: 0.25rem;
}

.alert-sm {
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
}

.teams-space-around {
  justify-content: space-around !important;
}

.teams-space-around .col-md-6 {
  flex: 0 0 auto;
  max-width: 45%;
}
</style>
